import { useEffect } from "react";
import { Switch, Route, Redirect } from "wouter";
import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { configureApiClient } from "@/lib/apiClient";
import { useOnboarding } from "@/hooks/useOnboarding";

import Dashboard from "@/pages/Dashboard";
import NewRFP from "@/pages/NewRFP";
import ProjectDetail from "@/pages/ProjectDetail";
import ResponseMatrix from "@/pages/ResponseMatrix";
import RFPSummaryPage from "@/pages/RFPSummary";
import AboutUs from "@/pages/AboutUs";
import AboutClient from "@/pages/AboutClient";
import Strategy from "@/pages/Strategy";
import UpdatesPage from "@/pages/Updates";
import SettingsIntegrations from "@/pages/SettingsIntegrations";
import SettingsTeam from "@/pages/SettingsTeam";
import SettingsTeamProfiles from "@/pages/SettingsTeamProfiles";
import Drafts from "@/pages/Drafts";
import SectionDetail from "@/pages/SectionDetail";
import FinalCheck from "@/pages/FinalCheck";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import NotFound from "@/pages/NotFound";
import {
  AboutUsStub,
  ContextBuilderStub,
  ProposalDraftsStub,
  QAReviewStub,
  RequiredDocumentsStub,
  ResearchBriefStub,
  StrategyBriefStub,
} from "@/pages/Stub";

/**
 * AppShell — wires the Clerk auth-token getter into our customFetch and
 * renders the protected routes. Mounted only when SignedIn.
 */
function AppShell() {
  const { getToken } = useAuth();

  useEffect(() => {
    configureApiClient(() => getToken());
  }, [getToken]);

  // First-run: load the demo project if the org has none.
  useOnboarding();

  return (
    <Switch>
      <Route path="/" component={Dashboard} />
      <Route path="/new" component={NewRFP} />

      {/* Org-level settings */}
      <Route path="/settings/integrations" component={SettingsIntegrations} />
      <Route path="/settings/team" component={SettingsTeam} />
      <Route path="/settings/team-profiles" component={SettingsTeamProfiles} />

      {/* Project-scoped routes */}
      <Route path="/projects/:id" component={ProjectDetail} />
      <Route path="/projects/:id/summary" component={RFPSummaryPage} />
      <Route path="/projects/:id/about-us" component={AboutUs} />
      <Route path="/projects/:id/about-client" component={AboutClient} />
      <Route path="/projects/:id/strategy" component={Strategy} />
      <Route path="/projects/:id/matrix" component={ResponseMatrix} />
      <Route path="/projects/:id/drafts" component={Drafts} />
      <Route path="/projects/:id/sections/:sectionId" component={SectionDetail} />
      <Route path="/projects/:id/attachments" component={RequiredDocumentsStub} />
      <Route path="/projects/:id/qa" component={FinalCheck} />
      <Route path="/projects/:id/updates" component={UpdatesPage} />

      {/* Legacy routes — kept for backwards compat but not in nav */}
      <Route path="/projects/:id/context" component={ContextBuilderStub} />
      <Route path="/projects/:id/research" component={ResearchBriefStub} />
      <Route path="/projects/:id/documents" component={RequiredDocumentsStub} />

      <Route component={NotFound} />
    </Switch>
  );
}

export default function App() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/login/:rest*" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/signup/:rest*" component={Signup} />

      <Route>
        <SignedIn>
          <AppShell />
        </SignedIn>
        <SignedOut>
          {/* Send unauthenticated users to our own /login page (which embeds
              Clerk's SignIn component). */}
          <Redirect to="/login" />
        </SignedOut>
      </Route>
    </Switch>
  );
}
